import React, { Component } from "react";
import PropTypes from "prop-types";

import Heading from "../Heading";
import ButtonRow from "../ButtonRow";

import IconContext from '../context/IconContext';

// Define the type of the React component being given via context
type SvgComponentProps = React.SVGProps<SVGSVGElement> 
// Create an interface containing the icons needed for this component
interface IIconContext {
    IconCancel: React.ComponentType<SvgComponentProps>;
    // To use custom SVGs, update the config to define these keys.
    IconModalCancel: React.ComponentType<SvgComponentProps>;
}

interface ModalProps extends React.HTMLAttributes<HTMLElement> {
    isHidden?: boolean;
    clickOutsideToClose?: boolean;
    title: string;
    actions?: React.ReactNode;
    // Baseline arguments
    children?: React.ReactNode;
    className?: string;
    // Functions
    onClose?: () => void;
}

/**
 * A modal that displays information in the middle of the screen overlayed on top of everything.
 * 
 * ## _
 * ### Arguments
 * @param {boolean} isHidden - Controls the visibility of the modal. Defaults to true.
 * @param {boolean} clickOutsideToClose - A flag that permits a user to click the outside area to close the modal.
 * @param {string} title - Text that appears at the top of the modal as a heading. 
 * @param {React.ReactNode} actions - Buttons to populate the button row at the bottom of the modal.
 * @param {React.ReactNode} children - The children of the modal. Node that populates the main area of the modal.
 * @param {string} className - CSS classes to apply to the modal.
 * 
 * ## _
 * ### Functions
 * @param {function} onClose - Function that is called when the modal is closed.
 * 
 * ## _
 * ### Icons
 * Declare these in your application's icon configuration.
 * @inner `IconModalCancel` - An optional icon override for the cancel button.
 */
class Modal extends React.Component<ModalProps> {
    // Establish the context and tell TypeScript that we're TOTALLY having it in the shape of this thing.
    static contextType = IconContext;
    context!: IIconContext;

    static propTypes = {
        isHidden: PropTypes.bool.isRequired,
        clickOutsideToClose: PropTypes.bool,
        title: PropTypes.string,
        actions: PropTypes.node,
        children: PropTypes.node,
        className: PropTypes.string,
        onClose: PropTypes.func,
    }
  
    static defaultProps = {
        isHidden: true,
        clickOutsideToClose: true,
        title: "Modal Title",
        className: "",
    }

    handleClickOutside = () => {
        const { clickOutsideToClose, onClose } = this.props;
        if(clickOutsideToClose && onClose){
            onClose();
        }
    }

    render() {
        const { isHidden, clickOutsideToClose, title, actions, children, className, onClose, ...rest } = this.props;
        const { IconCancel, IconModalCancel } = this.context;

        const CancelIcon = IconModalCancel || IconCancel;

        let classes = `o-modal`;
        classes += isHidden ? ` o-modal--hidden` : ` o-modal--shown`

        if (className) classes += ` ${className}`

        return (
            <div className={classes} {...rest}>
                <div className="o-modal__overlay" onClick={this.handleClickOutside} />
                <div className="o-modal__dialog">
                    <div className="o-modal__content">
                        <div className="o-modal__header">
                            <Heading size={3} className="o-modal__title">{ title }</Heading>
                            <button className="close o-modal__close">
                                <CancelIcon
                                    className="a-icon-link__icon"
                                    data-dismiss="modal"
                                    aria-label="Close"
                                    onClick={onClose}
                                />
                            </button>
                        </div>
                        <div className="o-modal__body">{ children }</div>
                        <div className="o-modal__footer">
                            <ButtonRow>{ actions }</ButtonRow>
                        </div>
                    </div>
                </div>
            </div>
        )
    }
}

export default Modal;